/**
 * Typed API client.
 *
 * Two rules this file exists to keep:
 *   1. the manage secret travels in an Authorization header and nowhere else — never a
 *      query string, never a body field, never a log;
 *   2. every failure becomes a typed `ApiFailure` so the UI can show a specific,
 *      non-alarming message instead of a stack trace.
 */

import type {
  ApiErrorBody,
  ApiErrorCode,
  CreateRoomRequest,
  CreateRoomResponse,
  ManageAction,
  ManageResponse,
  MyResponseView,
  PublicRoomView,
} from '../../shared/api.js';

export class ApiFailure extends Error {
  constructor(
    readonly code: ApiErrorCode | 'network_error',
    message: string,
    readonly field?: string,
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  adminSecret?: string;
  participantToken?: string;
  signal?: AbortSignal;
}

async function call<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.adminSecret) headers['Authorization'] = `Bearer ${options.adminSecret}`;
  if (options.participantToken) headers['X-Participant-Token'] = options.participantToken;

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'omit',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiFailure('network_error', 'We couldn’t reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    if (response.ok) return undefined as T;
    throw new ApiFailure('internal_error', 'Something went wrong. Try again in a moment.');
  }

  if (!response.ok) {
    const error = payload as ApiErrorBody;
    throw new ApiFailure(
      error.error ?? 'internal_error',
      error.message ?? 'Something went wrong.',
      error.field,
      error.retryAfter,
    );
  }

  return payload as T;
}

export function createRoom(request: CreateRoomRequest): Promise<CreateRoomResponse> {
  return call<CreateRoomResponse>('/api/rooms', { method: 'POST', body: request });
}

export function fetchRoom(roomId: string, signal?: AbortSignal): Promise<PublicRoomView> {
  return call<PublicRoomView>(`/api/rooms/${encodeURIComponent(roomId)}`, signal ? { signal } : {});
}

export function submitResponse(
  roomId: string,
  amountMinor: number,
  participantToken: string,
): Promise<PublicRoomView> {
  return call<PublicRoomView>(`/api/rooms/${encodeURIComponent(roomId)}/response`, {
    method: 'PUT',
    body: { amountMinor, participantToken },
  });
}

export function fetchMyResponse(roomId: string, participantToken: string): Promise<MyResponseView> {
  return call<MyResponseView>(`/api/rooms/${encodeURIComponent(roomId)}/my-response`, {
    participantToken,
  });
}

export function manageRoom(
  roomId: string,
  adminSecret: string,
  action: ManageAction,
): Promise<ManageResponse> {
  return call<ManageResponse>(`/api/rooms/${encodeURIComponent(roomId)}/manage`, {
    method: 'PATCH',
    adminSecret,
    body: action,
  });
}

export function deleteRoom(roomId: string, adminSecret: string): Promise<void> {
  return call<void>(`/api/rooms/${encodeURIComponent(roomId)}`, {
    method: 'DELETE',
    adminSecret,
  });
}
